import axios from 'axios'
import clsx from 'clsx'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import {KTCard, KTCardBody, KTIcon} from '../../../../../_websoft/helpers'
import {proizvodnjaPanelaIzvjestaj} from '../../../../api/izvjestaj'
import DrzavaDropdownPaneli from '../../../../components/DrzavaDropdown/DrzavaDropdownPaneli'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../../../components/Toast/Toast'
import TitleComponent from '../../../../components/TitleComponent/TitleComponent'

const minDatum = '2024-01-01'
const perPageOptions = [25, 50, 100, 200]
const formatDateInput = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const today = new Date()
const defaultDatumOd = `${today.getFullYear()}-01-01`
const defaultDatumDo = formatDateInput(today)
const numberFormatter = new Intl.NumberFormat('bs-BA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

type SelectValue = {
  name: string
  value: string | null
}

type AppliedFilters = {
  datumOd: string
  datumDo: string
  drzava: SelectValue
}

type PaginationState = {
  page: number
  perPage: number
  total: number
  totalPages: number
}

type IzvjestajSazetak = {
  planirana_kolicina?: string | null
  ukupna_kolicina: string | null
  prethodna_kolicina: string | null
  razlika_kolicina: string | null
  promjena_postotak: number | string | null
  prije_dvije_godine_kolicina: string | null
  razlika_prije_dvije_godine_kolicina: string | null
  promjena_prije_dvije_godine_postotak: number | string | null
}

type IzvjestajRow = {
  kupac?: string | null
  kupac_naziv?: string | null
  planirana_kolicina?: string | null
  ukupna_kolicina?: string | null
  prethodna_kolicina?: string | null
  razlika_kolicina?: string | null
  promjena_postotak?: number | string | null
  prije_dvije_godine_kolicina?: string | null
  razlika_prije_dvije_godine_kolicina?: string | null
  promjena_prije_dvije_godine_postotak?: number | string | null
  udio_postotak?: number | string | null
}

type ReportState = {
  rows: IzvjestajRow[]
  sazetak: IzvjestajSazetak | null
  period?: any
  rawResponse?: any
  filters: AppliedFilters
}

const emptyDrzava = {name: '', value: null}
const defaultPagination: PaginationState = {page: 1, perPage: 50, total: 0, totalPages: 1}

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null
  }

  const normalized = typeof value === 'string' ? value.replace(',', '.') : value
  const parsed = Number(normalized)

  return Number.isFinite(parsed) ? parsed : null
}

const formatDecimal = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)
  return parsed === null ? '—' : numberFormatter.format(parsed)
}

const formatPercent = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)
  return parsed === null ? '—' : `${numberFormatter.format(parsed)}%`
}

const formatChange = (value: string | number | null | undefined, isPercent = false) => {
  const parsed = toNumber(value)

  if (parsed === null) {
    return '—'
  }

  const sign = parsed > 0 ? '+' : parsed < 0 ? '−' : ''
  const formatted = numberFormatter.format(Math.abs(parsed))

  return `${sign}${formatted}${isPercent ? '%' : ''}`
}

const changeClass = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  return clsx({
    'text-success': parsed !== null && parsed > 0,
    'text-danger': parsed !== null && parsed < 0,
    'text-gray-700': parsed === null || parsed === 0,
  })
}

const getCustomerName = (row: IzvjestajRow) => {
  return row.kupac_naziv?.trim() || row.kupac?.trim() || 'Nepoznat kupac'
}

const getRows = (payload: any): IzvjestajRow[] => {
  if (Array.isArray(payload?.data)) {
    return payload.data
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data
  }

  if (Array.isArray(payload?.items)) {
    return payload.items
  }

  return []
}

const getMeta = (raw: any, payload: any, rows: IzvjestajRow[]) => {
  const dataMeta = !Array.isArray(payload?.data) ? payload?.data : null
  const meta = payload?.meta ?? payload?.pagination ?? raw?.meta ?? raw?.pagination ?? dataMeta ?? payload
  const total = Number(meta?.total ?? meta?.total_results ?? rows.length) || 0
  const perPage = Number(meta?.per_page ?? meta?.perPage ?? defaultPagination.perPage) || defaultPagination.perPage
  const page = Number(meta?.current_page ?? meta?.page ?? 1) || 1
  const totalPages =
    Number(meta?.last_page ?? meta?.total_pages ?? meta?.totalPage) || Math.max(1, Math.ceil(total / perPage))

  return {page, perPage, total, totalPages}
}

const getPayload = (raw: any) => {
  if (raw?.data && (raw.data.sazetak || raw.data.summary || raw.data.data || raw.data.items)) {
    return raw.data
  }

  return raw
}

const formatDisplayDate = (date: string) => {
  const [year, month, day] = date.slice(0, 10).split('-')

  return year && month && day ? `${day}-${month}-${year}` : date
}

const getFormattedPeriodLabel = (period: any, fallbackFrom: string, fallbackTo: string) => {
  const label = getPeriodLabel(period, fallbackFrom, fallbackTo)
  const dates = label.split(' - ')

  return dates.length === 2
    ? `${formatDisplayDate(dates[0])} - ${formatDisplayDate(dates[1])}`
    : label
}

const getPeriodLabel = (period: any, fallbackFrom: string, fallbackTo: string) => {
  const from = period?.datum_od ?? period?.datumOd ?? period?.from ?? fallbackFrom
  const to = period?.datum_do ?? period?.datumDo ?? period?.to ?? fallbackTo

  return from && to ? `${from} - ${to}` : '—'
}

const getYearRangeLabel = (datumOd: string, datumDo: string, offset = 0) => {
  const godinaOd = Number(datumOd.slice(0, 4))
  const godinaDo = Number(datumDo.slice(0, 4))

  if (!Number.isFinite(godinaOd) || !Number.isFinite(godinaDo)) {
    return ''
  }

  const from = godinaOd + offset
  const to = godinaDo + offset

  return from === to ? String(from) : `${from}-${to}`
}

const sumRows = (rows: IzvjestajRow[], field: keyof IzvjestajRow) => {
  let total = 0
  let hasValue = false

  rows.forEach((row) => {
    const value = toNumber(row[field])

    if (value !== null) {
      total += value
      hasValue = true
    }
  })

  return hasValue ? String(total) : null
}

const getTotalValue = (
  summaryValue: string | null | undefined,
  rows: IzvjestajRow[],
  field: keyof IzvjestajRow
) => summaryValue ?? sumRows(rows, field)

const getTotalPercent = (
  summaryValue: number | string | null | undefined,
  currentValue: string | number | null | undefined,
  previousValue: string | number | null | undefined
) => {
  if (summaryValue !== null && typeof summaryValue !== 'undefined' && summaryValue !== '') {
    return summaryValue
  }

  const current = toNumber(currentValue)
  const previous = toNumber(previousValue)

  return current !== null && previous !== null && previous !== 0
    ? ((current - previous) / Math.abs(previous)) * 100
    : null
}

const getTotalRow = (report: ReportState): IzvjestajRow => {
  const summary = report.sazetak
  const ukupnaKolicina = getTotalValue(summary?.ukupna_kolicina, report.rows, 'ukupna_kolicina')
  const prethodnaKolicina = getTotalValue(summary?.prethodna_kolicina, report.rows, 'prethodna_kolicina')
  const prijeDvijeGodineKolicina = getTotalValue(
    summary?.prije_dvije_godine_kolicina,
    report.rows,
    'prije_dvije_godine_kolicina'
  )

  return {
    kupac_naziv: 'Ukupno',
    planirana_kolicina: getTotalValue(summary?.planirana_kolicina, report.rows, 'planirana_kolicina'),
    ukupna_kolicina: ukupnaKolicina,
    prethodna_kolicina: prethodnaKolicina,
    razlika_kolicina: getTotalValue(summary?.razlika_kolicina, report.rows, 'razlika_kolicina'),
    promjena_postotak: getTotalPercent(summary?.promjena_postotak, ukupnaKolicina, prethodnaKolicina),
    prije_dvije_godine_kolicina: prijeDvijeGodineKolicina,
    razlika_prije_dvije_godine_kolicina: getTotalValue(
      summary?.razlika_prije_dvije_godine_kolicina,
      report.rows,
      'razlika_prije_dvije_godine_kolicina'
    ),
    promjena_prije_dvije_godine_postotak: getTotalPercent(
      summary?.promjena_prije_dvije_godine_postotak,
      prethodnaKolicina,
      prijeDvijeGodineKolicina
    ),
    udio_postotak: report.rows.length > 0 ? 100 : null,
  }
}

const IzvjestajPoKupcuDrzavi: React.FC = () => {
  const [datumOd, setDatumOd] = useState(defaultDatumOd)
  const [datumDo, setDatumDo] = useState(defaultDatumDo)
  const [drzava, setDrzava] = useState<SelectValue>(emptyDrzava)
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(null)
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination)
  const [report, setReport] = useState<ReportState | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const abortControllerRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  const uniqueRowKeys = useMemo(() => {
    const names = report?.rows.map((row) => getCustomerName(row)) ?? []
    return new Set(names).size === names.length
  }, [report])

  const fetchReport = async (filters: AppliedFilters, page: number, perPage: number) => {
    abortControllerRef.current?.abort()

    const controller = new AbortController()
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    abortControllerRef.current = controller
    setLoading(true)
    setError('')

    const params: any = {
      datum_od: filters.datumOd,
      datum_do: filters.datumDo,
      poredi_prethodnu_godinu: 'true',
      page,
      per_page: perPage,
    }

    if (filters.drzava.value) {
      params.drzava = filters.drzava.value
    }

    try {
      const response = await proizvodnjaPanelaIzvjestaj(params, controller.signal)

      if (requestId !== requestIdRef.current) {
        return
      }

      const payload = getPayload(response.data)
      const rows = getRows(payload)
      const meta = getMeta(response.data, payload, rows)

      setReport({
        rows,
        sazetak: payload?.sazetak ?? payload?.summary ?? null,
        period: payload?.period ?? payload?.periodi ?? payload?.periods,
        rawResponse: response.data,
        filters,
      })
      setPagination(meta)
    } catch (err: any) {
      if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') {
        return
      }

      console.error('Proizvodnja panela izvjestaj error:', err)

      const message = err?.response?.data?.message || 'Greska prilikom ucitavanja izvjestaja.'
      setError(message)
      notifyToast(false, message)
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }

  const validateFilters = () => {
    if (!datumOd || !datumDo) {
      notifyToast(false, 'Datum od i datum do su obavezni.')
      return false
    }

    if (datumOd > datumDo) {
      notifyToast(false, 'Datum od ne moze biti veci od datuma do.')
      return false
    }

    return true
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateFilters()) {
      return
    }

    const filters = {datumOd, datumDo, drzava}
    setAppliedFilters(filters)
    fetchReport(filters, 1, pagination.perPage)
  }

  const handlePageChange = (page: number) => {
    if (!appliedFilters || page < 1 || page > pagination.totalPages || page === pagination.page) {
      return
    }

    fetchReport(appliedFilters, page, pagination.perPage)
  }

  const handlePerPageChange = (perPage: number) => {
    const nextPagination = {...pagination, page: 1, perPage}
    setPagination(nextPagination)

    if (appliedFilters) {
      fetchReport(appliedFilters, 1, perPage)
    }
  }

  const startRow = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.perPage + 1
  const endRow = Math.min(pagination.page * pagination.perPage, pagination.total)
  const periodLabel = report
    ? getFormattedPeriodLabel(report.period, report.filters.datumOd, report.filters.datumDo)
    : ''
  const previousPeriodLabel = getFormattedPeriodLabel(
    report?.period?.prethodna_godina ?? report?.period?.prethodni_period,
    '',
    ''
  )
  const twoYearsAgoPeriodLabel = getFormattedPeriodLabel(
    report?.period?.prije_dvije_godine ?? report?.period?.period_prije_dvije_godine,
    '',
    ''
  )
  const currentYearLabel = report
    ? getYearRangeLabel(report.filters.datumOd, report.filters.datumDo)
    : ''
  const previousYearLabel = report
    ? getYearRangeLabel(report.filters.datumOd, report.filters.datumDo, -1)
    : ''
  const twoYearsAgoYearLabel = report
    ? getYearRangeLabel(report.filters.datumOd, report.filters.datumDo, -2)
    : ''
  const totalRow = report ? getTotalRow(report) : null

  return (
    <KTCard>
      <ToastContainer />

      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <TitleComponent name='Izvjestaj po kupcu / drzavi' />
        </div>
      </div>

      <KTCardBody className='border-top p-9'>
        <form onSubmit={handleSubmit}>
          <div className='row mb-6 align-items-end'>
            <div className='col-lg-2 col-md-6 col-sm-12 mb-6'>
              <label className='form-label fw-bold fs-6'>Datum od</label>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                value={datumOd}
                min={minDatum}
                onChange={(event) => setDatumOd(event.target.value)}
              />
            </div>

            <div className='col-lg-2 col-md-6 col-sm-12 mb-6'>
              <label className='form-label fw-bold fs-6'>Datum do</label>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                value={datumDo}
                min={minDatum}
                onChange={(event) => setDatumDo(event.target.value)}
              />
            </div>

            <div className='col-lg-3 col-md-6 col-sm-12 mb-6'>
              <label className='form-label fw-bold fs-6'>Drzava</label>
              <DrzavaDropdownPaneli
                value={drzava.value ?? ''}
                selectName='drzava'
                onChange={(name, value) => setDrzava({name, value})}
              />
            </div>

            <div className='col-lg-2 col-md-6 col-sm-12 mb-6'>
              <button type='submit' className='btn btn-primary w-100' disabled={loading}>
                {loading ? (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Ucitavanje...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  <>
                    <KTIcon iconName='magnifier' className='fs-2' />
                    Prikazi
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {!report && !loading && !error && (
          <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6'>
            <div className='fw-bold text-gray-700'>Odaberite filtere i kliknite Prikazi.</div>
          </div>
        )}

        {error && (
          <div className='notice d-flex flex-column bg-light-danger rounded border-danger border border-dashed p-6 mb-8'>
            <div className='fw-bold text-gray-800 mb-3'>{error}</div>
            <button
              type='button'
              className='btn btn-sm btn-danger align-self-start'
              disabled={loading || !appliedFilters}
              onClick={() =>
                appliedFilters && fetchReport(appliedFilters, pagination.page, pagination.perPage)
              }
            >
              Pokusaj ponovo
            </button>
          </div>
        )}

        {loading && !report && <LoadingComponent />}

        {report && (
          <>
            <div className='d-flex flex-wrap justify-content-between align-items-center gap-4 mb-7'>
              <div>
                <div className='text-muted fw-semibold fs-7 text-uppercase'>Primijenjeni period</div>
                <div className='fs-3 fw-bolder text-gray-900'>{periodLabel}</div>
              </div>

              {loading && (
                <span className='badge badge-light-primary fs-7 py-3 px-4'>Osvjezavanje podataka...</span>
              )}
            </div>

            <div className='row g-6 mb-9'>
              <div className='col-xl-3 col-md-6'>
                <div className='border border-gray-300 rounded p-6 h-100 bg-light-success'>
                  <div className='text-muted fw-semibold mb-2'>Za proizvest</div>
                  <div className='fs-1 fw-bolder text-gray-900'>
                    {formatDecimal(report.sazetak?.planirana_kolicina)}
                  </div>
                </div>
              </div>

              <div className='col-xl-3 col-md-6'>
                <div className='border border-gray-300 rounded p-6 h-100 bg-light-primary'>
                  <div className='text-muted fw-semibold mb-2'>
                    Odabrani period {currentYearLabel && `(${currentYearLabel})`}
                  </div>
                  <div className='fs-1 fw-bolder text-gray-900'>
                    {formatDecimal(report.sazetak?.ukupna_kolicina)}
                  </div>
                </div>
              </div>

              <div className='col-xl-3 col-md-6'>
                <div className='border border-gray-300 rounded p-6 h-100 bg-light-info'>
                  <div className='text-muted fw-semibold mb-2'>
                    Isti period prethodne godine {previousYearLabel && `(${previousYearLabel})`}
                  </div>
                  {previousPeriodLabel !== '—' && (
                    <div className='text-muted fs-7 mb-2'>{previousPeriodLabel}</div>
                  )}
                  <div className='fs-2 fw-bolder text-gray-900 mb-2'>
                    {formatDecimal(report.sazetak?.prethodna_kolicina)}
                  </div>
                  <div className='text-muted fs-7'>
                    Promjena {currentYearLabel || 'odabranog perioda'} u odnosu na{' '}
                    {previousYearLabel || 'prethodnu godinu'}
                  </div>
                  <div className={clsx('fw-bold', changeClass(report.sazetak?.razlika_kolicina))}>
                    {formatChange(report.sazetak?.razlika_kolicina)} /{' '}
                    {formatChange(report.sazetak?.promjena_postotak, true)}
                  </div>
                </div>
              </div>

              <div className='col-xl-3 col-md-6'>
                <div className='border border-gray-300 rounded p-6 h-100 bg-light-warning'>
                  <div className='text-muted fw-semibold mb-2'>
                    Prethodna godina u odnosu na godinu prije{' '}
                    {previousYearLabel && twoYearsAgoYearLabel && `(${previousYearLabel} / ${twoYearsAgoYearLabel})`}
                  </div>
                  {twoYearsAgoPeriodLabel !== '—' && (
                    <div className='text-muted fs-7 mb-2'>{twoYearsAgoPeriodLabel}</div>
                  )}
                  <div className='fs-2 fw-bolder text-gray-900 mb-2'>
                    {formatDecimal(report.sazetak?.prije_dvije_godine_kolicina)}
                  </div>
                  <div className='text-muted fs-7'>
                    Promjena {previousYearLabel || 'prethodne godine'} u odnosu na{' '}
                    {twoYearsAgoYearLabel || 'godinu prije'}
                  </div>
                  <div
                    className={clsx(
                      'fw-bold',
                      changeClass(report.sazetak?.razlika_prije_dvije_godine_kolicina)
                    )}
                  >
                    {formatChange(report.sazetak?.razlika_prije_dvije_godine_kolicina)} /{' '}
                    {formatChange(report.sazetak?.promjena_prije_dvije_godine_postotak, true)}
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex flex-wrap justify-content-between align-items-center gap-4 mb-4'>
              <div className='text-muted fw-semibold'>
                Prikazano {startRow}-{endRow} od {pagination.total}
              </div>

              <div className='d-flex align-items-center gap-3'>
                <label className='text-muted fw-semibold'>Po stranici</label>
                <select
                  className='form-select form-select-sm form-select-solid w-100px'
                  value={pagination.perPage}
                  disabled={loading}
                  onChange={(event) => handlePerPageChange(Number(event.target.value))}
                >
                  {perPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {report.rows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gy-4'>
                  <thead>
                    <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                      <th className='text-white ps-4 min-w-250px' rowSpan={2}>
                        Kupac
                      </th>
                      <th className='text-white text-end min-w-140px' rowSpan={2}>
                        Za proizvest
                      </th>
                      <th className='text-white text-end min-w-170px' rowSpan={2}>
                        Kolicina - odabrani period {currentYearLabel && `(${currentYearLabel})`}
                      </th>
                      <th className='text-white text-center' colSpan={3}>
                        Poredjenje {currentYearLabel && previousYearLabel && `${currentYearLabel} / ${previousYearLabel}`}
                      </th>
                      <th className='text-white text-center' colSpan={3}>
                        Poredjenje {previousYearLabel && twoYearsAgoYearLabel && `${previousYearLabel} / ${twoYearsAgoYearLabel}`}
                      </th>
                      <th className='text-white text-end pe-4 min-w-110px' rowSpan={2}>
                        Udio (%)
                      </th>
                    </tr>
                    <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                      <th className='text-white text-end min-w-170px'>
                        Kolicina - prethodna godina {previousYearLabel && `(${previousYearLabel})`}
                      </th>
                      <th className='text-white text-end min-w-190px'>
                        Razlika {currentYearLabel && previousYearLabel && `${currentYearLabel} / ${previousYearLabel}`}
                      </th>
                      <th className='text-white text-end min-w-200px'>
                        {currentYearLabel && previousYearLabel && `${currentYearLabel} / ${previousYearLabel}`} (%)
                      </th>
                      <th className='text-white text-end min-w-190px'>
                        Kolicina - prije dvije godine {twoYearsAgoYearLabel && `(${twoYearsAgoYearLabel})`}
                      </th>
                      <th className='text-white text-end min-w-230px'>
                        Razlika {previousYearLabel && twoYearsAgoYearLabel && `${previousYearLabel} / ${twoYearsAgoYearLabel}`}
                      </th>
                      <th className='text-white text-end min-w-240px'>
                        {previousYearLabel && twoYearsAgoYearLabel && `${previousYearLabel} / ${twoYearsAgoYearLabel}`} (%)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row, index) => (
                      <tr key={uniqueRowKeys ? getCustomerName(row) : `${getCustomerName(row)}-${index}`}>
                        <td className='ps-4 fw-bold text-gray-900'>{getCustomerName(row)}</td>
                        <td className='text-end'>{formatDecimal(row.planirana_kolicina)}</td>
                        <td className='text-end'>{formatDecimal(row.ukupna_kolicina)}</td>
                        <td className='text-end'>{formatDecimal(row.prethodna_kolicina)}</td>
                        <td className={clsx('text-end fw-bold', changeClass(row.razlika_kolicina))}>
                          {formatChange(row.razlika_kolicina)}
                        </td>
                        <td className={clsx('text-end fw-bold', changeClass(row.promjena_postotak))}>
                          {formatChange(row.promjena_postotak, true)}
                        </td>
                        <td className='text-end'>{formatDecimal(row.prije_dvije_godine_kolicina)}</td>
                        <td
                          className={clsx(
                            'text-end fw-bold',
                            changeClass(row.razlika_prije_dvije_godine_kolicina)
                          )}
                        >
                          {formatChange(row.razlika_prije_dvije_godine_kolicina)}
                        </td>
                        <td
                          className={clsx(
                            'text-end fw-bold',
                            changeClass(row.promjena_prije_dvije_godine_postotak)
                          )}
                        >
                          {formatChange(row.promjena_prije_dvije_godine_postotak, true)}
                        </td>
                        <td className='text-end pe-4'>{formatPercent(row.udio_postotak)}</td>
                      </tr>
                    ))}
                    {totalRow && (
                      <tr className='fw-bolder bg-light-primary border-top border-2 border-gray-400'>
                        <td className='ps-4 text-gray-900'>{getCustomerName(totalRow)}</td>
                        <td className='text-end'>{formatDecimal(totalRow.planirana_kolicina)}</td>
                        <td className='text-end'>{formatDecimal(totalRow.ukupna_kolicina)}</td>
                        <td className='text-end'>{formatDecimal(totalRow.prethodna_kolicina)}</td>
                        <td className={clsx('text-end', changeClass(totalRow.razlika_kolicina))}>
                          {formatChange(totalRow.razlika_kolicina)}
                        </td>
                        <td className={clsx('text-end', changeClass(totalRow.promjena_postotak))}>
                          {formatChange(totalRow.promjena_postotak, true)}
                        </td>
                        <td className='text-end'>{formatDecimal(totalRow.prije_dvije_godine_kolicina)}</td>
                        <td
                          className={clsx(
                            'text-end',
                            changeClass(totalRow.razlika_prije_dvije_godine_kolicina)
                          )}
                        >
                          {formatChange(totalRow.razlika_prije_dvije_godine_kolicina)}
                        </td>
                        <td
                          className={clsx(
                            'text-end',
                            changeClass(totalRow.promjena_prije_dvije_godine_postotak)
                          )}
                        >
                          {formatChange(totalRow.promjena_prije_dvije_godine_postotak, true)}
                        </td>
                        <td className='text-end pe-4'>{formatPercent(totalRow.udio_postotak)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                <div className='fw-bold text-gray-700'>Nema podataka za odabrane filtere.</div>
              </div>
            )}

            <div className='d-flex flex-wrap justify-content-between align-items-center gap-4 mt-6'>
              <div className='text-muted fw-semibold'>
                Stranica {pagination.page} od {pagination.totalPages}
              </div>

              <ul className='pagination mb-0'>
                <li className={clsx('page-item', (loading || pagination.page === 1) && 'disabled')}>
                  <button type='button' className='page-link' onClick={() => handlePageChange(1)}>
                    First
                  </button>
                </li>
                <li className={clsx('page-item', (loading || pagination.page === 1) && 'disabled')}>
                  <button
                    type='button'
                    className='page-link'
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Previous
                  </button>
                </li>
                <li className='page-item active'>
                  <button type='button' className='page-link'>
                    {pagination.page}
                  </button>
                </li>
                <li
                  className={clsx(
                    'page-item',
                    (loading || pagination.page >= pagination.totalPages) && 'disabled'
                  )}
                >
                  <button
                    type='button'
                    className='page-link'
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                  </button>
                </li>
                <li
                  className={clsx(
                    'page-item',
                    (loading || pagination.page >= pagination.totalPages) && 'disabled'
                  )}
                >
                  <button
                    type='button'
                    className='page-link'
                    onClick={() => handlePageChange(pagination.totalPages)}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </div>
          </>
        )}
      </KTCardBody>
    </KTCard>
  )
}

export default IzvjestajPoKupcuDrzavi
